import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { notificationService } from '../../../features/reports/services/notificationService'
import type { Notification } from '../../../features/reports/services/notificationService'
import { logout } from './authSlice'

interface NotificationsState {
  items: Notification[]
  unreadCount: number
  loading: boolean
  error: string | null
}

const initialState: NotificationsState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
}

export const fetchNotifications = createAsyncThunk('notifications/fetchAll', async () => {
  return await notificationService.getNotifications()
})

export const fetchUnreadCount = createAsyncThunk('notifications/fetchUnreadCount', async () => {
  return await notificationService.getUnreadCount()
})

export const markNotificationAsRead = createAsyncThunk(
  'notifications/markAsRead',
  async (id: string) => {
    await notificationService.markAsRead(id)
    return id
  }
)

export const markAllNotificationsAsRead = createAsyncThunk(
  'notifications/markAllAsRead',
  async () => {
    await notificationService.markAllAsRead()
  }
)

const notificationsSlice = createSlice({
  name: 'notifications',
  initialState,
  reducers: {
    clearNotifications: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch all notifications
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false
        state.items = action.payload
        state.unreadCount = action.payload.filter((n) => !n.is_read).length
      })
      .addCase(fetchNotifications.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch notifications'
      })

    // Fetch unread count
    builder.addCase(fetchUnreadCount.fulfilled, (state, action) => {
      state.unreadCount = action.payload
    })

    // Mark single as read
    builder.addCase(markNotificationAsRead.fulfilled, (state, action) => {
      const notification = state.items.find((n) => n.id === action.payload)
      if (notification && !notification.is_read) {
        notification.is_read = true
        notification.read_at = new Date().toISOString()
        state.unreadCount = Math.max(0, state.unreadCount - 1)
      }
    })

    // Mark all as read
    builder.addCase(markAllNotificationsAsRead.fulfilled, (state) => {
      state.items.forEach((n) => {
        n.is_read = true
        n.read_at = n.read_at ?? new Date().toISOString()
      })
      state.unreadCount = 0
    })

    // Clear on logout
    builder.addCase(logout.fulfilled, () => initialState)
  },
})

export const { clearNotifications } = notificationsSlice.actions
export const notificationsReducer = notificationsSlice.reducer
